import { App } from '@capacitor/app';
import { isNativePlatform } from './platformService.js';

function getOpenModalOrDrawer() {
  if (typeof document === 'undefined') return null;
  const selectors = [
    '.calendly-overlay',
    'dialog[open]',
    '[role="dialog"][data-state="open"]',
    '[role="alertdialog"][data-state="open"]',
  ];
  return selectors
    .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
    .filter((element) => element.getBoundingClientRect().width > 0 || element.getBoundingClientRect().height > 0)
    .at(-1) ?? null;
}

function closeOpenModalOrDrawer() {
  const target = getOpenModalOrDrawer();
  if (!target) return false;

  const event = new KeyboardEvent('keydown', {
    key: 'Escape',
    code: 'Escape',
    bubbles: true,
    cancelable: true,
  });
  target.dispatchEvent(event);
  document.dispatchEvent(event);

  // Radix prevents Escape's default when it handles dismissal. A dialog may
  // have returned to an earlier step, so do not click that step's Close too.
  if (event.defaultPrevented) return true;

  window.setTimeout(() => {
    if (getOpenModalOrDrawer() !== target) return;
    const closeButton = target.querySelector(
      '.calendly-popup-close, [aria-label="Close"], [aria-label="Close modal"], [aria-label^="Close "], [data-dialog-close], button.close, button.x',
    );
    closeButton?.click();
  }, 0);

  return true;
}

function hasActiveRecording() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('native-recording-active')
    || Boolean(document.querySelector('[data-native-recording-active="true"]'));
}

export function listenForAppLifecycle(onChange) {
  if (!isNativePlatform()) return () => {};

  const listeners = [];
  let disposed = false;

  Promise.all([
    App.addListener('appStateChange', ({ isActive }) => onChange(isActive ? 'foreground' : 'background')),
    App.addListener('resume', () => onChange('resume')),
    App.addListener('pause', () => onChange('pause')),
  ]).then((handles) => {
    if (disposed) {
      handles.forEach((handle) => void handle.remove());
      return;
    }
    listeners.push(...handles);
  });

  return () => {
    disposed = true;
    listeners.forEach((handle) => void handle.remove());
  };
}

export function exitNativeApp() {
  if (!isNativePlatform()) return;
  void App.exitApp();
}

export function listenForAndroidBackButton({ history, isRootRoute, onRootBack, onRecordingBack }) {
  if (!isNativePlatform()) return () => {};

  let handle;
  let disposed = false;
  App.addListener('backButton', ({ canGoBack }) => {
    if (closeOpenModalOrDrawer()) return;
    if (hasActiveRecording()) {
      onRecordingBack?.();
      return;
    }
    if (isRootRoute?.()) {
      onRootBack?.();
      return;
    }
    if (canGoBack) {
      history.back();
      return;
    }
    onRootBack?.();
  }).then((listener) => {
    if (disposed) {
      void listener.remove();
      return;
    }
    handle = listener;
  });

  return () => {
    disposed = true;
    void handle?.remove();
  };
}
